import { useEffect, useState } from 'react';
import { Copy, Link2, Loader2, Trash2 } from 'lucide-react';
import {
  createAdminReplyPlaybookPageShare,
  fetchAdminReplyPlaybookPageShares,
  revokeAdminReplyPlaybookPageShare,
  type AdminReplyPlaybookShare,
} from '@/api/adminReplyPlaybooks';

interface AdminReplyPlaybookPageSharePanelProps {
  tenantSlug: string;
  totalItems: number;
}

function formatShareExpiry(value?: string) {
  if (!value) {
    return 'Без ограничения срока';
  }
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

export function AdminReplyPlaybookPageSharePanel({ tenantSlug, totalItems }: AdminReplyPlaybookPageSharePanelProps) {
  const [shares, setShares] = useState<AdminReplyPlaybookShare[]>([]);
  const [expiresAt, setExpiresAt] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetchAdminReplyPlaybookPageShares(tenantSlug)
      .then(setShares)
      .catch(() => setError('Не удалось загрузить ссылки на страницу'))
      .finally(() => setLoading(false));
  }, [tenantSlug]);

  const handleCreate = async () => {
    setSaving(true);
    setError(null);
    try {
      const share = await createAdminReplyPlaybookPageShare({
        tenant_slug: tenantSlug,
        expires_at: expiresAt || undefined,
      });
      setShares((current) => [share, ...current]);
      setExpiresAt('');
      await navigator.clipboard.writeText(share.share_url);
    } catch {
      setError('Не удалось создать ссылку на страницу');
    } finally {
      setSaving(false);
    }
  };

  const handleRevoke = async (share: AdminReplyPlaybookShare) => {
    if (!window.confirm('Отозвать публичную ссылку на всю страницу подсказок?')) {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await revokeAdminReplyPlaybookPageShare(share.token);
      setShares((current) => current.map((item) => (item.token === share.token ? { ...item, is_active: false } : item)));
    } catch {
      setError('Не удалось отозвать ссылку');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="rounded-2xl border border-shell-border bg-shell-card p-4 shadow-sm">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="inline-flex items-center gap-2 text-sm font-semibold text-shell-strong">
            <Link2 className="h-4 w-4" />
            Шеринг всей страницы подсказок
          </div>
          <p className="mt-1 text-sm text-shell-muted">
            Публичная ссылка откроет все опубликованные подсказки для `{tenantSlug}`. Сейчас на странице {totalItems} шт.
          </p>
        </div>
        <div className="grid shrink-0 gap-2 sm:grid-cols-[180px_auto] sm:items-end">
          <label className="text-sm text-shell-muted">
            Срок действия
            <input
              type="date"
              value={expiresAt}
              onChange={(event) => setExpiresAt(event.target.value)}
              className="mt-1 w-full rounded-xl border border-shell-border bg-shell-elevated px-3 py-2 text-sm text-shell-strong outline-none focus:border-shell-primary"
            />
          </label>
          <button
            type="button"
            onClick={handleCreate}
            disabled={saving || totalItems === 0}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-shell-primary px-4 py-2 text-sm font-medium text-white hover:bg-shell-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Создать и скопировать
          </button>
        </div>
      </div>

      {error && <div className="mt-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
      {loading && <div className="mt-3 text-sm text-shell-muted">Загружаем ссылки...</div>}

      {!loading && shares.length > 0 && (
        <div className="mt-4 space-y-2">
          {shares.map((share) => (
            <div key={share.token} className="rounded-xl border border-shell-border bg-shell-elevated p-3">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <div className="min-w-0">
                  <a
                    href={share.share_url}
                    target="_blank"
                    rel="noreferrer"
                    className="block truncate text-sm font-medium text-shell-primary hover:underline"
                  >
                    {share.share_url}
                  </a>
                  <div className="mt-1 text-xs text-shell-muted">
                    {formatShareExpiry(share.expires_at)} · {share.is_active ? 'активна' : 'отозвана'}
                  </div>
                </div>
                <div className="flex shrink-0 flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => navigator.clipboard.writeText(share.share_url)}
                    className="inline-flex items-center gap-1 rounded-lg border border-shell-border px-2.5 py-1.5 text-xs text-shell-strong hover:bg-shell-hover"
                  >
                    <Copy className="h-3.5 w-3.5" />
                    Копировать
                  </button>
                  {share.is_active && (
                    <button
                      type="button"
                      onClick={() => handleRevoke(share)}
                      disabled={saving}
                      className="inline-flex items-center gap-1 rounded-lg border border-red-200 px-2.5 py-1.5 text-xs text-red-700 hover:bg-red-50 disabled:opacity-60"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      Отозвать
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
